import { A } from '@adornis/base/env-info.js';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { defer, type Observable, of, switchMap, map } from 'rxjs';
import { Company } from '../db/company.js';
import { MatchingWeekPermissions, MatchingWeekRole } from '../db/enums/roles.js';
import { CompanyUserAddedMail } from '../mails/company-mails.js';
import { assertCallerIsAdmin } from './admin-guard.js';

type MatchingWeekUser = User & { hasPermission(permission: MatchingWeekPermissions, context?: string): boolean };

export const getAllCompanies = registerQuery({
  operationName: 'getAllCompanies',
  type: () => [Company],
  params: [],
  resolve: () => async (gqlFields: BaseQLSelectionSet<Company>) => {
    const collection = await scopedCollection(Company);
    return collection.find({}).project({ name: 1, address: 1, sector: 1, website: 1 }).toArray() as Promise<Company[]>;
  },
});

export const countCompanies = registerQuery({
  operationName: 'countCompanies',
  type: () => Int,
  resolve: () => async () => {
    return (await scopedCollection(Company)).countDocuments();
  },
});

export const getCompanyByUserID = registerQuery({
  operationName: 'getCompanyByUserID',
  type: () => Company,
  params: [{ name: 'userID', type: () => ID }],
  resolve: (userID: string) => async (gqlFields: BaseQLSelectionSet<Company>) => {
    if (!context.serverContext) {
      const user = await context.user();
      if (!user) throw new NotLoggedInError();
      const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (!isAdmin && userID !== user._id) throw new Error('Not authorized to view another user company');
      if (!isAdmin) userID = user._id;
    }
    const collection = await scopedCollection(Company);
    return collection.findOne({ userIDs: userID }) as Promise<Company>;
  },
});

export const getUsersByCompanyID = registerQuery({
  operationName: 'getUsersByCompanyID',
  type: () => [User],
  params: [{ name: 'companyID', type: () => ID }],
  resolve: (companyID: string) => async (gqlFields: BaseQLSelectionSet<User>) => {
    if (!context.serverContext) {
      const user = (await context.user()) as MatchingWeekUser | null;
      if (!user) throw new NotLoggedInError();
      const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (!isAdmin && !user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, companyID)) {
        throw new Error('Not authorized for this company');
      }
    }
    const collection = await scopedCollection(User);
    return collection.find({ companyID: companyID }).toArray() as Promise<User[]>;
  },
});

export const deleteCompany = registerMutation({
  operationName: 'deleteCompany',
  type: () => Boolean,
  params: [{ name: 'companyID', type: () => ID }],
  resolve: (companyID: string) => async () => {
    await assertCallerIsAdmin();
    const collection = await scopedCollection(Company);
    const result = await collection.deleteOne({ _id: companyID as any });
    if (result.deletedCount && result.deletedCount > 0) return true;
    throw new Error('Company not found');
  },
});

export const setCompanyStatus = registerMutation({
  operationName: 'setCompanyStatus',
  type: () => Boolean,
  params: [
    { name: 'companyID', type: () => ID },
    { name: 'isActive', type: () => Boolean },
  ],
  resolve: (companyID: string, isActive: boolean) => async () => {
    await assertCallerIsAdmin();
    const collection = await scopedCollection(Company);
    const result = await collection.updateOne({ _id: companyID as any }, { $set: { isActive } });
    if (result.matchedCount === 0) throw new Error('Company not found');
    return true;
  },
});

export const addUserToCompany = registerMutation({
  operationName: 'addUserToCompany',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'companyID', type: () => ID },
  ],
  resolve: (userID: string, companyID: string) => async () => {
    await assertCallerIsAdmin();
    // TODO users can only be added to one company, we will remove them from all other companies here
    // ? Do we need to warn the user about this?
    // we will ignore this for now, needs to be fixed for next year if this continues

    const user = await getUserByID(userID)({ email: 1, username: 1, firstName: 1, lastName: 1 });
    if (!user) throw new Error('User not found');

    const collection = await scopedCollection(Company);
    const result = await collection.updateOne({ _id: companyID as any }, { $addToSet: { userIDs: userID } });
    if (result.modifiedCount && result.modifiedCount > 0) {
      const rawCol = mongo().collection<EntityData<Company>>(Company._collectionName);
      await rawCol.updateMany({ _id: { $ne: companyID }, userIDs: userID }, { $pull: { userIDs: userID } });

      await CompanyUserAddedMail(user, A.absoluteUrl('/company')).send({
        mailer: await mailer,
        recipients: [new Recipient(user.email)],
      });

      return true;
    }

    // TODO handle if user was already part of company
    throw new Error('Company not found');
  },
});

export const removeUserFromCompany = registerMutation({
  operationName: 'removeUserFromCompany',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'companyID', type: () => ID },
  ],
  resolve: (userID: string, companyID: string) => async () => {
    await assertCallerIsAdmin();
    const collection = await scopedCollection(Company);
    const result = await collection.updateOne({ _id: companyID as any }, { $pull: { userIDs: userID as any } } as any);
    if (result.modifiedCount && result.modifiedCount > 0) return true;
    // If no modification, either the company doesn't exist or the user was not in the list
    throw new Error('Company not found or user not assigned to company');
  },
});

export const subscribeAllCompanyUsers = registerSubscription({
  operationName: 'subscribeAllCompanyUsers',
  type: () => [User],
  resolve: (skip: number, limit: number, filter: AdornisFilter) => (gqlFields: BaseQLSelectionSet<User>) => {
    // TODO (low prio) we're using the search for the companyID filter
    const companyID = filter.search;
    return defer(async () => {
      if (!companyID) throw new Error('Company ID is required');
      if (!context.serverContext) {
        const user = (await context.user()) as MatchingWeekUser | null;
        if (!user) throw new NotLoggedInError();
        const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
        if (!isAdmin && !user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, companyID)) {
          throw new Error('Not authorized for this company');
        }
      }
      return companyID;
    }).pipe(
      switchMap(authorizedCompanyID =>
        watchQuery<EntityData<Company>>(
          Company._collectionName,
          { _id: authorizedCompanyID },
          { projection: { _id: 1, userIDs: 1 } },
        ),
      ),
      switchMap(companies => {
        const company = companies[0];
        if (!company) return of([]);

        return watchQuery<EntityData<User>>(
          User._collectionName,
          { _id: { $in: company.userIDs ?? [] } },
          { skip, limit },
        );
      }),
    ) as Observable<User[]>;
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

export const getCompaniesByIDs = registerQuery({
  operationName: 'getCompaniesByIDs',
  type: () => [Company],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Company>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Company, ids, gqlFields),
});

export const subscribeCompaniesByIDs = registerSubscription({
  operationName: 'subscribeCompaniesByIDs',
  type: () => [Company],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Company>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Company, ids, gqlFields),
});

export const getAllCompanys = registerQuery({
  operationName: 'getAllCompanys',
  type: () => [Company],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Company>>(gqlFields: SelSet) =>
      readAllEntities(Company, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllCompanies = registerSubscription({
  operationName: 'subscribeAllCompanies',
  type: () => [Company],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Company>>(gqlFields: SelSet) =>
      watchAllEntities(Company, gqlFields, { skip, limit, excludeIDs }),
});

export const getCompanyByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Company>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getCompaniesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeCompanyByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Company>>(gqlFields: SelSet) =>
    id ? subscribeCompaniesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
